import React, { useCallback, useEffect, useState } from "react";
import { Package, Plus, Pencil, Trash2, Check, X, Loader2, FileSpreadsheet } from "lucide-react";
import { api, API, KATEGORI, loadKategori, fmtDate } from "@/lib/apiClient";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const empty = { kategori: "", jenis: "", merk: "", ukuran: "", harga: "", harga_jual: "" };
const rp = (v) => `Rp ${Number(v || 0).toLocaleString("id-ID")}`;

const FIELDS = [
  { key: "jenis", label: "Jenis", ph: "mis. Flexi China" },
  { key: "merk", label: "Merk", ph: "mis. Zhongyi" },
  { key: "ukuran", label: "Ukuran / Satuan", ph: "mis. 3.2m / roll" },
  { key: "harga", label: "Harga Beli (Rp)", ph: "25000" },
  { key: "harga_jual", label: "Harga Jual (Rp)", ph: "35000" },
];

const KatSelect = ({ value, onChange, options, testid, className = "" }) => (
  <select
    data-testid={testid}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className={`w-full rounded-xl bg-slate-900/70 border border-white/10 px-3 text-sm outline-none focus:ring-2 focus:ring-teal-500 ${className}`}
  >
    <option value="">— pilih kategori —</option>
    {options.map((k) => (
      <option key={k} value={k}>{k}</option>
    ))}
  </select>
);

export const MaterialManager = () => {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [draft, setDraft] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [kategoriList, setKategoriList] = useState(KATEGORI);

  useEffect(() => {
    loadKategori().then((k) => setKategoriList([...k]));
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/materials");
      setRows(data);
    } catch {
      toast.error("Gagal memuat data bahan");
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/materials", { ...form, harga: Number(form.harga) || 0 });
      toast.success("Bahan ditambahkan");
      setForm(empty);
      await load();

    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menambah bahan");
    } finally {
      setBusy(false);
    }
  };

  const save = async (id) => {
    try {
      await api.put(`/materials/${id}`, { ...draft, harga: Number(draft.harga) || 0 });
      toast.success("Bahan diperbarui");
      setEditId(null);
      await load();

    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan");
    }
  };

  const hapus = async (row) => {
    if (!window.confirm(`Hapus bahan ${row.kategori} · ${row.jenis}? Riwayat stoknya juga dihapus.`))
      return;
    try {
      await api.delete(`/materials/${row.id}`);
      toast.success("Bahan dihapus");
      await load();

    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menghapus");
    }
  };

  return (
    <div className="space-y-5" data-testid="material-manager">
      <div className="grid gap-4 sm:grid-cols-3" data-testid="material-summary">
        <div className="glass rounded-2xl p-5">
          <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Jenis Bahan</p>
          <div className="mt-2 font-display text-2xl font-light text-slate-100">{rows.length}</div>
        </div>
        <div className="glass rounded-2xl p-5">
          <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Jumlah Stok</p>
          <div className="mt-2 font-display text-2xl font-light text-teal-300">
            {rows.reduce((a, r) => a + Number(r.stok || 0), 0).toLocaleString("id-ID")}
          </div>
        </div>
        <div className="glass rounded-2xl p-5">
          <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Nilai Stok</p>
          <div className="mt-2 font-display text-2xl font-light text-amber-300">
            {rp(rows.reduce((a, r) => a + Number(r.nilai_stok || 0), 0))}
          </div>
        </div>
      </div>

      <form onSubmit={submit} className="glass rounded-2xl p-5 md:p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Package className="w-5 h-5 text-teal-400" />
          <h3 className="font-display text-lg font-semibold">Entri Bahan Baru</h3>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
          <div>
            <span className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Kategori</span>
            <KatSelect
              testid="material-kategori-select"
              value={form.kategori}
              onChange={(v) => setForm({ ...form, kategori: v })}
              options={kategoriList}
              className="mt-1 h-11"
            />
          </div>
          {FIELDS.map((f) => (
            <div key={f.key}>
              <span className="text-[10px] uppercase tracking-[0.16em] text-slate-500">{f.label}</span>
              <Input
                data-testid={`material-${f.key}-input`}
                placeholder={f.ph}
                value={form[f.key]}
                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                className="mt-1 h-11 bg-slate-900/70 border-white/10"
              />
            </div>
          ))}
        </div>
        <Button
          data-testid="material-submit-btn"
          type="submit"
          disabled={busy}
          className="h-11 rounded-xl bg-teal-600 hover:bg-teal-500 px-6 font-semibold"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-2" /> Simpan Bahan</>}
        </Button>
      </form>

      <div className="glass rounded-2xl p-5 md:p-6 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h4 className="font-display text-sm font-semibold uppercase tracking-wider text-slate-300">
            Daftar Bahan ({rows.length})
          </h4>
          <Button
            data-testid="material-export-btn"
            onClick={() => window.open(`${API}/materials/export?auth=${localStorage.getItem("pas_token")}`, "_blank")}
            className="h-10 rounded-xl bg-emerald-600 hover:bg-emerald-500 px-4 text-xs font-semibold"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Download Excel
          </Button>
        </div>
        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full text-xs">
            <thead className="bg-slate-900/80 text-slate-400 uppercase text-[10px] tracking-wider">
              <tr>
                {["Kategori", "Jenis", "Merk", "Ukuran", "Harga Beli", "Harga Jual", "Stok", "Nilai Stok", "Tanggal Entri", "User Entri", "Aksi"].map((h) => (
                  <th key={h} className="px-3 py-2 text-left font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody data-testid="material-list">
              {rows.length === 0 && (
                <tr>
                  <td colSpan={11} className="px-3 py-8 text-center text-slate-500">
                    Belum ada bahan. Tambahkan lewat form di atas.
                  </td>
                </tr>
              )}
              {rows.map((r) =>
                editId === r.id ? (
                  <tr key={r.id} className="border-t border-white/5 bg-slate-900/60">
                    {["kategori", "jenis", "merk", "ukuran", "harga", "harga_jual"].map((k) => (
                      <td key={k} className="px-2 py-2">
                        {k === "kategori" ? (
                          <KatSelect
                            testid="material-edit-kategori"
                            value={draft.kategori}
                            onChange={(v) => setDraft({ ...draft, kategori: v })}
                            options={kategoriList}
                            className="h-9 text-xs"
                          />
                        ) : (
                          <Input
                            data-testid={`material-edit-${k}`}
                            value={draft[k]}
                            onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
                            className="h-9 bg-slate-900/70 border-white/10 text-xs"
                          />
                        )}
                      </td>
                    ))}
                    <td className="px-3 py-2 text-slate-400">{r.stok}</td>
                    <td className="px-3 py-2 text-slate-400">{rp(r.nilai_stok)}</td>
                    <td className="px-3 py-2 text-slate-500">{fmtDate(r.tanggal_entri)}</td>
                    <td className="px-3 py-2 text-slate-500">{r.entri_oleh || "-"}</td>
                    <td className="px-3 py-2">
                      <div className="flex gap-2">
                        <button
                          type="button"
                          data-testid={`material-save-${r.id}`}
                          onClick={() => save(r.id)}
                          className="rounded-lg bg-emerald-600 p-1.5 hover:bg-emerald-500"
                        >
                          <Check className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditId(null)}
                          className="rounded-lg border border-white/10 bg-slate-800 p-1.5 hover:bg-slate-700"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <tr key={r.id} className="border-t border-white/5 hover:bg-slate-800/40">
                    <td className="px-3 py-2 text-slate-200 font-medium">{r.kategori}</td>
                    <td className="px-3 py-2">{r.jenis}</td>
                    <td className="px-3 py-2 text-slate-400">{r.merk || "-"}</td>
                    <td className="px-3 py-2 text-slate-400">{r.ukuran || "-"}</td>
                    <td className="px-3 py-2">{rp(r.harga)}</td>
                    <td className="px-3 py-2 text-amber-300">{rp(r.harga_jual)}</td>
                    <td className="px-3 py-2 font-semibold text-teal-300">{r.stok}</td>
                    <td className="px-3 py-2">{rp(r.nilai_stok)}</td>
                    <td className="px-3 py-2 text-slate-500">{fmtDate(r.tanggal_entri)}</td>
                    <td className="px-3 py-2 text-slate-500">{r.entri_oleh || "-"}</td>
                    <td className="px-3 py-2">
                      <div className="flex gap-2">
                        <button
                          type="button"
                          data-testid={`material-edit-btn-${r.id}`}
                          onClick={() => {
                            setEditId(r.id);
                            setDraft({
                              kategori: r.kategori,
                              jenis: r.jenis,
                              merk: r.merk || "",
                              ukuran: r.ukuran || "",
                              harga: r.harga,
                              harga_jual: r.harga_jual || 0,
                            });
                          }}
                          className="rounded-lg border border-white/10 bg-slate-800/70 p-1.5 hover:bg-slate-700"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          data-testid={`material-delete-btn-${r.id}`}
                          onClick={() => hapus(r)}
                          className="rounded-lg border border-rose-500/25 bg-rose-500/10 p-1.5 text-rose-300 hover:bg-rose-500/20"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
